/**
 * Governance Hub item card — unified card for Memo, CAP, and Calendar obligations.
 *
 * A contract-driven item answers the reader's first question up front: WHICH
 * part of the contract demands this. The card leads with the contract's name
 * and section, offers the verbatim clause on demand, names the owner as a
 * person rather than a battery of dropdowns (editing folds behind "Change"),
 * and carries the same thread and reminder actions the mandate app taught
 * people to expect: talk to the owner where the work is, email them from the
 * same place, and see the whole exchange in context.
 */
import React, { useState } from 'react';
import ThreadPanel from './ThreadPanel';

const API = process.env.REACT_APP_API_URL;

const SOURCE_STYLES = {
  memo: 'bg-violet-100 text-violet-700 border-violet-200',
  cap: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  calendar: 'bg-sky-100 text-sky-700 border-sky-200',
};

const STATUS_STYLES = {
  overdue: 'bg-red-100 text-red-700 border-red-200',
  due_soon: 'bg-amber-100 text-amber-800 border-amber-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
  no_action_needed: 'bg-slate-100 text-slate-600 border-slate-200',
  in_progress: 'bg-sky-100 text-sky-700 border-sky-200',
  upcoming: 'bg-blue-50 text-blue-700 border-blue-200',
};

const statusLabel = (s) => {
  const n = (s || '').toLowerCase();
  const map = { overdue: 'Overdue', due_soon: 'Due soon', completed: 'Completed', no_action_needed: 'No action needed', in_progress: 'In progress', upcoming: 'Upcoming' };
  return map[n] || 'Upcoming';
};

const sourceLabel = (s, item) => {
  const n = (s || '').toLowerCase();
  if (n === 'memo') return 'Memo';
  if (n === 'cap') return 'CAP';
  // Calendar obligations: label by program, not a blanket "CMS".
  const program = item?.obligation_type || item?.type || '';
  if (program === 'State Medicaid (SMAC)') return 'Medicaid';
  if (program === 'CMS') return 'Medicare';
  return program || 'Calendar';
};

export default function ItemCard({
  item,
  effectiveStatus,
  formatDateDisplay,
  onSelect,
  isCompliance,
  renderObligationAccountable,
  isCalendarNonActionable,
  isCalendarObligationCompleted,
  handleStatusChange,
  handleActionableChange,
  handleFetchGuidance,
  statusUpdatingId,
  actionableUpdatingId,
  guidanceByObligationId,
  calendarStatusLabel,
  onOpenInEmbeddedView,
}) {
  const source = item.source || 'calendar';
  const regulator = item.authority || item.obligation_type || item.type || item.classification;
  const guidance = guidanceByObligationId?.[item.id];
  const isMemo = source === 'memo';
  const isCap = source === 'cap';
  // A contract-backed item: the Hub row materialized from a State Medicaid
  // contract obligation, which carries the contract's own language.
  const isContract = !!item.smac_obligation_id;
  const [showClause, setShowClause] = useState(false);
  const [editOwner, setEditOwner] = useState(false);
  const [remindState, setRemindState] = useState(null); // null | 'busy' | 'sent' | error string

  const sendReminder = async (e) => {
    e.stopPropagation();
    if (remindState === 'busy') return;
    setRemindState('busy');
    try {
      const r = await fetch(`${API}/api/smac/obligations/${item.smac_obligation_id}/remind`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      setRemindState(d.success ? 'sent' : (d.message || 'The reminder could not be sent.'));
    } catch {
      setRemindState('The reminder could not be sent.');
    }
  };

  const cardBg =
    isCalendarNonActionable?.(item)
      ? 'bg-emerald-50/60 border-emerald-200'
      : effectiveStatus === 'completed'
        ? 'bg-slate-50/80 border-slate-200'
        : effectiveStatus === 'overdue'
          ? 'bg-red-50/40 border-red-200/80'
          : effectiveStatus === 'due_soon'
            ? 'bg-amber-50/40 border-amber-200/80'
            : 'bg-white border-slate-200';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(item)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect?.(item)}
      className={`rounded-xl border p-4 shadow-sm transition-all hover:shadow-md hover:border-slate-300/80 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40 ${cardBg}`}
    >
      <div className="flex justify-between items-start gap-2 mb-2">
        <span className="text-xs font-semibold text-slate-500">{formatDateDisplay(item)}</span>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          <span className={`inline-flex px-2 py-0.5 rounded-lg text-[10px] font-semibold uppercase border ${SOURCE_STYLES[source] || SOURCE_STYLES.calendar}`}>
            {sourceLabel(source, item)}
          </span>
          {isCalendarNonActionable?.(item) && (
            <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">Non-actionable</span>
          )}
          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${STATUS_STYLES[effectiveStatus] || STATUS_STYLES.upcoming}`}>
            {isCap ? statusLabel(effectiveStatus) : calendarStatusLabel?.(item) ?? statusLabel(effectiveStatus)}
          </span>
          {Number(item.deliverable_count) > 0 && (
          <span className="inline-flex items-center gap-0.5 text-xs text-slate-500" title={`${item.deliverable_count} deliverable${Number(item.deliverable_count) === 1 ? '' : 's'} attached`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
            {item.deliverable_count}
          </span>
        )}
          {item.visibility_reason && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500" title={`Why you see this: ${item.visibility_reason}`}>
              <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {item.visibility_reason}
            </span>
          )}
        </div>
      </div>

      <h3 className={`text-sm font-semibold mb-2 break-words ${effectiveStatus === 'completed' ? 'text-slate-500' : 'text-slate-800'}`}>
        {(item.title && String(item.title).trim()) || '—'}
      </h3>

      {isContract ? (
        <div className="mb-3">
          <div className="text-xs text-slate-700">
            <span className="font-medium text-slate-800">{item.contract_name || 'State Medicaid contract'}</span>
            {item.contract_state ? <span className="text-slate-500"> ({item.contract_state})</span> : null}
            {item.contract_section ? (
              <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-semibold">
                {item.contract_section}
              </span>
            ) : null}
          </div>
          {item.contract_duty && (
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{item.contract_duty}</p>
          )}
          {item.contract_quote && (
            <div onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setShowClause((v) => !v)}
                className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 mt-1"
              >
                {showClause ? 'Hide the contract language' : 'What the contract says'}
              </button>
              {showClause && (
                <blockquote className="mt-1.5 border-l-2 border-indigo-300 bg-indigo-50/50 pl-3 pr-2 py-2 text-[12px] text-slate-700 italic leading-relaxed rounded-r">
                  “{item.contract_quote}”
                </blockquote>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="mb-3">
          <span className="text-xs text-slate-500">Type</span>
          <span className="text-xs text-slate-700 ml-1">{regulator || '—'}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="flex-1 min-w-[100px]">
          <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500 block mb-0.5">Accountable</span>
          {isMemo ? (
            <div className="text-xs text-slate-700">
              {(Array.isArray(item.departments) && item.departments.length > 0) ? item.departments.join(', ') : '—'}
              {item.owner_email && <span className="block text-slate-600 font-mono text-[11px] truncate" title={item.owner_email}>{item.owner_email}</span>}
            </div>
          ) : isCap ? (
            <div className="text-xs text-slate-700">
              {item.owner_name && <span className="block">{item.owner_name}</span>}
              {item.owner_email && <span className="block text-slate-600 font-mono text-[11px] truncate" title={item.owner_email}>{item.owner_email}</span>}
              {!item.owner_name && !item.owner_email && '—'}
            </div>
          ) : isContract && !editOwner ? (
            // The owner is a person, not a stack of dropdowns. The controls
            // stay one click away for compliance, exactly like the mandate app.
            <div className="text-xs text-slate-700" onClick={(e) => e.stopPropagation()}>
              {(item.contract_owner_name || item.assignee_name) ? (
                <>
                  <span className="block font-medium">{item.contract_owner_name || item.assignee_name}</span>
                  {(item.contract_owner_email || item.assignee_email) && (
                    <span className="block text-slate-500 font-mono text-[11px] truncate" title={item.contract_owner_email || item.assignee_email}>
                      {item.contract_owner_email || item.assignee_email}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-slate-400">No owner yet</span>
              )}
              {isCompliance && (
                <button type="button" onClick={() => setEditOwner(true)}
                  className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium mt-0.5 block">
                  Change
                </button>
              )}
            </div>
          ) : (
            <div onClick={(e) => e.stopPropagation()}>
              {renderObligationAccountable?.(item, true)}
              {isContract && (
                <button type="button" onClick={() => setEditOwner(false)}
                  className="text-[11px] text-slate-500 hover:text-slate-700 font-medium mt-0.5 block">
                  Done
                </button>
              )}
            </div>
          )}
        </div>
        {!isMemo && !isCap && source === 'calendar' && (
          <div className="min-w-[80px]">
            <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500 block mb-0.5">Actionable</span>
            <select
              value={item.actionable === false ? 'no' : 'yes'}
              onChange={(e) => e.stopPropagation() || handleActionableChange?.(item.id, e.target.value === 'yes')}
              disabled={actionableUpdatingId === item.id}
              onClick={(e) => e.stopPropagation()}
              className="text-xs bg-white border border-slate-200 rounded px-2 py-1 w-full"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        )}
      </div>

      {isMemo && item.memo_url && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenInEmbeddedView?.(item.memo_url); }}
          className="text-xs text-violet-600 hover:text-violet-800 font-medium text-left block"
        >
          Open memo →
        </button>
      )}
      {isCap && item.cap_url && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenInEmbeddedView?.(item.cap_url); }}
          className="text-xs text-emerald-600 hover:text-emerald-800 font-medium text-left block"
        >
          Open CAP →
        </button>
      )}

      {source === 'calendar' && (
        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
          <button
            type="button"
            onClick={(e) => e.stopPropagation() || handleStatusChange?.(item.id, isCalendarObligationCompleted?.(item) ? 'upcoming' : 'completed')}
            disabled={statusUpdatingId === item.id}
            className={`p-1.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-violet-500/40 ${
              isCalendarObligationCompleted?.(item) ? 'bg-emerald-100 border-emerald-400 text-emerald-700' : 'border-slate-300 text-slate-400 hover:border-slate-400'
            }`}
            title={isCalendarObligationCompleted?.(item) ? 'Mark not complete' : 'Mark complete'}
          >
            {isCalendarObligationCompleted?.(item) ? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            )}
          </button>
          {!item.ai_guidance_status && (
            guidance?.loading ? (
              <span className="text-xs text-slate-400">Loading…</span>
            ) : (
              <button
                type="button"
                onClick={(e) => e.stopPropagation() || handleFetchGuidance?.(item.id)}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Get AI guidance
              </button>
            )
          )}
        </div>
      )}

      {isMemo && Array.isArray(item.action_items) && item.action_items.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <span className="text-xs text-slate-500">{item.action_items.length} action{item.action_items.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {isContract && (
        <div className="mt-2 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {isCompliance && (item.contract_owner_email || item.assignee_email) && (
              remindState === 'sent' ? (
                <span className="text-xs text-emerald-600 font-medium">Reminder sent to the owner</span>
              ) : (
                <button type="button" onClick={sendReminder} disabled={remindState === 'busy'}
                  title="Email the owner about this item now — the same reminder the schedule sends, and it lands in the thread"
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50">
                  {remindState === 'busy' ? 'Sending…' : 'Email the owner'}
                </button>
              )
            )}
          </div>
          {remindState && remindState !== 'busy' && remindState !== 'sent' && (
            <p className="text-xs text-red-600 mt-1">{remindState}</p>
          )}
          <ThreadPanel obligationId={item.smac_obligation_id} />
        </div>
      )}
    </div>
  );
}
